/**
 * Small moderator-only launcher for manual Facebook observations.
 * It reads the existing event-source registry and never accesses Facebook.
 */
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { MessageSquarePlus } from "lucide-react";

import FacebookSignalDialog from "@/components/FacebookSignalDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const modHeaders = () => ({ Authorization: `Bearer ${sessionStorage.getItem("mod_token")}` });
const FACEBOOK_TYPES = new Set(["facebook_page", "facebook_group_hint", "facebook_manual"]);

export default function FacebookManualSignalPanel() {
  const [sources, setSources] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [dialogSource, setDialogSource] = useState(null);

  useEffect(() => {
    let active = true;
    axios.get(`${API}/mod/event-sources`, { headers: modHeaders() })
      .then(({ data }) => {
        if (active) setSources(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        if (active) setSources([]);
      });
    return () => { active = false; };
  }, []);

  const facebookSources = useMemo(
    () => sources.filter((source) => FACEBOOK_TYPES.has(source.typ)),
    [sources],
  );
  const selected = facebookSources.find((source) => source.id === selectedId) || null;

  if (facebookSources.length === 0) return null;

  return (
    <section className="bg-parchment border border-warm-border rounded-sm p-4 mb-3">
      <div className="flex flex-col sm:flex-row sm:items-end gap-3">
        <div className="flex-1">
          <label className="block text-xs font-medium text-ink mb-1">Manuell Facebook-observation</label>
          <select value={selectedId} onChange={(e) => setSelectedId(e.target.value)} className="w-full text-sm px-3 py-2 border border-warm-border rounded-sm bg-white">
            <option value="">Välj registrerad Facebook-källa…</option>
            {facebookSources.map((source) => (
              <option key={source.id} value={source.id}>
                {source.namn} · {source.monitoring_mode || source.trust || source.typ}
              </option>
            ))}
          </select>
        </div>
        <button type="button" disabled={!selected} onClick={() => setDialogSource(selected)} className="inline-flex items-center justify-center gap-1 text-xs px-3 py-2 border border-pine bg-pine text-cream rounded-sm disabled:opacity-50">
          <MessageSquarePlus size={14} /> Registrera signal
        </button>
      </div>
      <p className="mt-2 text-[11px] text-muted-foreground">Inget läses automatiskt från Facebook. Funktionen sparar bara moderatorns egen observation i den kanoniska granskningskön.</p>

      {dialogSource && (
        <FacebookSignalDialog
          source={dialogSource}
          onClose={() => setDialogSource(null)}
          onSaved={() => setDialogSource(null)}
        />
      )}
    </section>
  );
}
